/**
 * Dialogs API — role-play dialog exercises
 *
 * Endpoints (proxied via /api → api.langplat.com):
 *   GET  /dialogs/all          → Dialog[]  (raw array, no envelope)
 *   GET  /dialog/:id           → { dialog, answer? }  or  Dialog  (legacy compat)
 *   GET  /dialog/:id/:userId   → { dialog, answer? }
 *   POST /dialogs/create       → Dialog
 *   POST /dialogs/update       → Dialog
 *   POST /dialogs/delte/:id    → (delete — note typo in backend)
 *   POST /dialog/:id/answer/:userId  { answersMap } → (save student answers)
 */
import apiClient from './client';
import type { Dialog, DialogAnswer } from './types';

const DialogsAPI = {
  /** Get all dialogs (own + public). Raw array from backend. */
  async getAllDialogs(): Promise<Dialog[]> {
    const { data } = await apiClient.get<Dialog[]>('/dialogs/all');
    if (!Array.isArray(data)) return [];
    return data;
  },

  /** Get a single dialog and current user's saved answers */
  async getDialog(id: string, userId?: string): Promise<{ dialog: Dialog; answer?: DialogAnswer }> {
    const url = userId ? `/dialog/${id}/${userId}` : `/dialog/${id}`;
    const { data } = await apiClient.get<{ dialog?: Dialog; answer?: DialogAnswer } | Dialog>(url);
    // Backend may return `{ dialog, answer }` or just the raw Dialog object
    if (data && 'dialog' in data && data.dialog) {
      return data as { dialog: Dialog; answer?: DialogAnswer };
    }
    return { dialog: data as Dialog, answer: undefined };
  },

  /** Create a new dialog */
  async createDialog(payload: Partial<Dialog>): Promise<Dialog> {
    const { data } = await apiClient.post<Dialog>('/dialogs/create', payload);
    return data;
  },

  /** Update an existing dialog. Backend takes the whole object with id. */
  async updateDialog(id: string, payload: Partial<Dialog>): Promise<Dialog> {
    const { data } = await apiClient.post<Dialog>('/dialogs/update', { id, ...payload });
    return data;
  },

  /** Soft-delete a dialog — moves to trash (7-day bin) */
  async deleteDialog(id: string): Promise<void> {
    await apiClient.post(`/dialogs/delete/${id}`);
  },

  /** List dialogs currently in trash (deleted within last 7 days) */
  async getTrash(): Promise<Dialog[]> {
    const { data } = await apiClient.get<Dialog[]>('/dialogs/trash');
    return data ?? [];
  },

  /** Restore a dialog from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/dialogs/${id}/restore`);
  },

  /** Permanently delete a dialog (bypass trash) */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/dialogs/${id}/permanent`);
  },



  /**
   * Save media fields (audioUrl, imageUrls) directly to Parse.
   * The main REST API backend silently drops these fields, so we persist
   * them ourselves using the Parse REST API.
   * Safe to call even if Parse is unreachable — errors are swallowed.
   */
  async saveDialogMedia(
    id: string,
    audioUrl: string,
    imageUrls: string[],
  ): Promise<void> {
    try {
      // audioUrl/imageUrls are now returned by transformDialog on backend
      // Use /dialogs/update which does $set: {...params} — passes all fields through
      const patch: Record<string, unknown> = { id };
      if (audioUrl)         patch.audioUrl   = audioUrl;
      else                  patch.audioUrl   = null;   // clear
      if (imageUrls.length) patch.imageUrls  = imageUrls;
      else                  patch.imageUrls  = [];     // clear
      await apiClient.post('/dialogs/update', patch);
    } catch (e) {
      console.warn('[DialogsAPI] saveDialogMedia failed (non-fatal):', e);
    }
  },

  /**
   * Fetch only the media fields from the backend for a single dialog.
   * Used in openForEdit to supplement the main API response.
   */
  async getDialogMedia(id: string): Promise<{ audioUrl?: string; imageUrls?: string[] }> {
    try {
      const { dialog } = await this.getDialog(id);
      return {
        audioUrl:  (dialog as any).audioUrl  ?? undefined,
        imageUrls: (dialog as any).imageUrls ?? undefined,
      };
    } catch (e) {
      console.warn('[DialogsAPI] getDialogMedia failed (non-fatal):', e);
      return {};
    }
  },

  /** Clone a dialog — creates a copy with '(копия)' appended to the name */
  async cloneDialog(id: string): Promise<Dialog> {
    const { dialog: original } = await this.getDialog(id);
    const { id: _id, objectId: _oid, createdAt: _ca, updatedAt: _ua, ...rest } = original as any;
    return this.createDialog({ ...rest, name: `${original.name ?? ''} (копия)` });
  },

  /** Save student answers map (messageId → recorded audio URL) */
  async saveAnswer(dialogId: string, userId: string, answersMap: Record<string, string>): Promise<void> {
    await apiClient.post(`/dialog/${dialogId}/answer/${userId}`, { answersMap });
  },
};

export default DialogsAPI;
